import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FieldPath, UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DateTime } from "luxon";
import { Box, Dialog, DialogContent, styled } from "@mui/material";

import { usePortalContext } from "@/contexts/PortalProvider";

import { useAppDispatch } from "@/hooks/useAppDispatch";

import { AppDispatch } from "@/data/store";
import { Passenger } from "@/data/models/passenger.model";
import { TripLegType, type ReservationValidations } from "@/data/models/reservation.model";
import { ServicePlan } from "@/data/models/service-plan.model";
import type { TransportationAttribute } from "@/data/models/transportation-attribute.model";
import {
  ReservationFormInput,
  reservationFormSchema,
  buildEmptyReservationFormValues,
  type ReservationFormValues,
} from "./schema/reservationForm.schema";

import reservationUtils from "@/services/reservationUtils";

import * as lookupSlice from "@/data/lookup.slice";

import {
  ReservationFormMachinesProvider,
  useReservationFormMachines,
} from "./ReservationFormMachinesContext";
import { ReservationFormDebug } from "./useDebugReservationMachines";

import ReservationDialogHeader from "@/components/home/reservations/ReservationDialogHeader";
import { ReservationTripLegForm } from "./ReservationTripLegForm";
import { ReservationMemberForm } from "./ReservationMemberForm";
import { ReservationSummary } from "./ReservationSummary";
import RequestOverrideDialog from "./prompts/RequestOverrideDialog";
import ConfirmationDialog from "../ConfirmationDialog";

const PREFIX = 'CreateReservationDialog';

const classes = {
  content: `${PREFIX}-content`,
  pages: `${PREFIX}-pages`,
};

/** Paths from `getReservationFormFieldErrors`, e.g. `trips[1].pickupDateTime`. */
function tripLegIndexFromFormFieldPath(field: string): number | undefined {
  const m = /^trips\[(\d+)\]\./.exec(field);
  if (m == null) return undefined;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : undefined;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    padding: 0,
    overflow: 'visible',
  },
  [`& .${classes.pages}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    maxWidth: '1152px',
    margin: '0 auto',
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(18),
      paddingRight: theme.spacing(18),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
  },
}));

function ReservationFormSubmit({
  form,
  onSubmit,
}: {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
  onSubmit: (
    values: ReservationFormValues,
    options: { saveAsDraft: boolean },
  ) => Promise<void>;
}) {
  const { mainSnapshot } = useReservationFormMachines();

  const wasSuccessRef = useRef(false);
  useEffect(() => {
    if (!mainSnapshot.matches('success')) {
      wasSuccessRef.current = false;
      return;
    }
    if (wasSuccessRef.current) return;
    wasSuccessRef.current = true;
    if (onSubmit) {
      onSubmit(form.getValues() as ReservationFormValues, {
        saveAsDraft: Boolean(mainSnapshot.context.saveAsDraft),
      });
    }
  }, [mainSnapshot, onSubmit, form]);

  return null;
}

interface CreateReservationDialogProps {
  open: boolean;
  onClose: () => void;
  passenger: Passenger | null;
  servicePlans: ServicePlan[];
  transportationAttributes?: TransportationAttribute[];
}

const CreateReservationDialog = ({
  open,
  onClose,
  passenger,
  servicePlans,
  transportationAttributes,
}: CreateReservationDialogProps) => {
  const { t } = useTranslation('common');
  const dispatch: AppDispatch = useAppDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const { isPortal, isClientPortal } = usePortalContext();

  const form = useForm<ReservationFormInput, unknown, ReservationFormValues>({
    resolver: zodResolver(reservationFormSchema),
    defaultValues: buildEmptyReservationFormValues(
      passenger,
      transportationAttributes,
    ),
    mode: 'onSubmit',
  });
  const { isDirty } = form.formState;
  const pickUpTime = form.watch('trips.0.pickupDateTime');
  const appointmentDate = form.watch('trips.0.appointmentDate');

  useEffect(() => {
    if (!open) return;
    form.reset(
      buildEmptyReservationFormValues(passenger, transportationAttributes),
    );
  }, [open, passenger?.passengerUUID, transportationAttributes]);

  const applyTripLegValidationRef = useRef<
    (validations: ReservationValidations) => void
  >(() => {});

  /**
   * Validation endpoint results are applied to the form here.
   */
  useEffect(() => {
    applyTripLegValidationRef.current = (
      validations: ReservationValidations,
    ) => {
      // Update the form values returned from validation results. Apparently only timezones
      for (const update of validations.tripLegUpdates) {
        const idx = update.tripLegSequence - 1;
        if (update.pickUpAddress?.timeZone) {
          if (form.getValues('trips')[idx] == null) continue;
          if (
            form.getValues('trips')[idx].pickupTimeZone !==
            update.pickUpAddress.timeZone
          ) {
            form.setValue(
              `trips.${idx}.pickupTimeZone`,
              update.pickUpAddress.timeZone,
              { shouldValidate: true },
            );
          }
        }
        if (update.dropOffAddress?.timeZone) {
          if (form.getValues('trips')[idx] == null) continue;
          if (
            form.getValues('trips')[idx].dropoffTimeZone !==
            update.dropOffAddress.timeZone
          ) {
            form.setValue(
              `trips.${idx}.dropoffTimeZone`,
              update.dropOffAddress.timeZone,
              { shouldValidate: true },
            );
          }
        }
      }
      // Set field specific errors from validation results
      form.clearErrors(['trips', 'genericErrors']);
      if (validations.errors.length > 0) {
        const tripType = form.getValues('tripType');
        const messages = reservationUtils.getReservationFormFieldErrors(
          t,
          tripType === TripLegType.RoundTrip,
          validations,
        );
        const tripsSnapshot = form.getValues('trips');
        const genericErrors: string[] = [];

        for (const message of messages) {
          if (message.field === 'GenericError') {
            genericErrors.push(message.message);
            continue;
          }

          const legIndex = tripLegIndexFromFormFieldPath(message.field);
          if (legIndex != null && tripsSnapshot[legIndex] == null) {
            continue;
          }

          const fieldPath = message.field as FieldPath<ReservationFormInput>;
          if (form.getValues(fieldPath) == null) continue;

          form.setError(fieldPath, {
            type: 'manual',
            message: message.message,
          });
        }

        if (genericErrors.length > 0) {
          form.setError('genericErrors', {
            type: 'manual',
            message: Array.from(new Set(genericErrors)).join('\n'),
          });
        }
      } else {
        form.clearErrors();
      }
    };
  }, [form]);

  const tripLegServiceDate =
    (pickUpTime instanceof DateTime && pickUpTime.isValid
      ? pickUpTime.toISODate()
      : null) ??
    (appointmentDate instanceof DateTime && appointmentDate.isValid
      ? appointmentDate.toISODate()
      : null) ??
    (typeof appointmentDate === 'string' && appointmentDate.trim()
      ? appointmentDate
      : null) ??
    undefined;
  const overridableServicePlans = servicePlans.filter(
    ({ effectiveStartDate, effectiveEndDate }) =>
      tripLegServiceDate != null &&
      effectiveStartDate <= tripLegServiceDate &&
      (!effectiveEndDate || tripLegServiceDate <= effectiveEndDate),
  );

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleReservationSubmit = useCallback(
    async (
      _values: ReservationFormValues,
      _options: { saveAsDraft: boolean },
    ) => {
      form.reset(
        buildEmptyReservationFormValues(passenger, transportationAttributes),
      );
      setActiveStep(0);
      onClose();
    },
    [form, onClose, passenger, transportationAttributes],
  );

  const handlePrevious = (evt: any) => {
    evt.preventDefault();
    setActiveStep(activeStep - 1);
  };

  const handleNext = (evt: any) => {
    evt.preventDefault();
    setActiveStep(activeStep + 1);
  };

  const handleCloseDialog = () => {
    if (isDirty) {
      setConfirmDialogOpen(true);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    // Fetch all Create Reservation dialog data that isn't passed via props
    dispatch(lookupSlice.fetchTransportationAttributeMapAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('timeZone'));
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegPassengerNoteType'));
    dispatch(lookupSlice.fetchLookupItemsAsync('tripLegDriverNoteType'));
  }, []);

  return (
    <StyledDialog open={open} onClose={onClose} fullScreen>
      <ReservationDialogHeader
        passengerName={
          isPortal
            ? undefined
            : `${passenger?.firstName} ${passenger?.lastName}`
        }
        title={t('reservation.createTrip')}
        steps={[
          t('reservation.tripDetailsStep'),
          t('reservation.member'),
          t('reservation.summary'),
        ]}
        activeStep={activeStep}
        isValid={true}
        step={handleStep}
        open={handleCloseDialog}
      />
      <DialogContent className={classes.content}>
        <Box className={classes.pages}>
          <ReservationFormMachinesProvider
            clientBC={passenger?.clientBC ?? ''}
            passengerUUID={passenger?.passengerUUID ?? ''}
            passengerPhone={
              passenger
                ? reservationUtils.getPassengerPhone(passenger)
                : undefined
            }
            applyTripLegValidationResultsToForm={() =>
              applyTripLegValidationRef.current
            }>
            <ReservationFormDebug form={form} />
            <ReservationFormSubmit
              form={form}
              onSubmit={handleReservationSubmit}
            />
            <RequestOverrideDialog
              servicePlans={overridableServicePlans}
              reservationForm={form}
            />
            {activeStep === 0 && (
              <ReservationTripLegForm
                form={form}
                onNext={handleNext}
                servicePlans={servicePlans}
              />
            )}
            {activeStep === 1 && (
              <ReservationMemberForm
                form={form}
                transportationAttributes={transportationAttributes ?? []}
                onNext={handleNext}
                onPrevious={handlePrevious}
              />
            )}
            {activeStep === 2 && (
              <ReservationSummary form={form} onPrevious={handlePrevious} />
            )}
          </ReservationFormMachinesProvider>
        </Box>
      </DialogContent>
      <ConfirmationDialog
        title={t('common.discardChanges')}
        message={t('common.possibleDataLoss')}
        open={confirmDialogOpen}
        positiveAction={onClose}
        negativeAction={() => setConfirmDialogOpen(false)}
      />
    </StyledDialog>
  );
};

export { CreateReservationDialog };
